import { GRAPHQL_AUTH_MODE } from "@aws-amplify/api";
import { API } from "aws-amplify";
import { useDispatch, useSelector } from "react-redux";
import {
  setListing,
  setListingRushDays,
  setOperationFilters,
  setSearchText,
  setSelected,
} from "../store/ducks/transaction";
import { HeadCell } from "../models/dataTable";
import {
  CreateTransactionInput,
  Transaction,
  UpdateTransactionInput,
} from "../models/GQL_API";
import useApp from "./useApp";
import { formatDateToYYYYMMDDHHMMSS, getDayName } from "../helpers/utils";
import useConcept from "./useConcept";
import useUser from "./useUser";
import { TransactionListingVariables } from "../models/app";
import {
  getTransaction,
  listTransactions,
  transactionByUser,
} from "../graphql/queries";
import {
  createTransaction,
  deleteTransaction,
  updateTransaction,
} from "../graphql/mutations";
import { LoyaltyOperations } from "../constants/enums";

const useResource = (listingName: string, singleName: string) => {
  const dispatch = useDispatch();
  const { session, showConfirm, showError } = useApp();
  const { conceptsGetName } = useConcept("concepts", "concept");
  const { guestsGetName } = useUser("guests", "guest");

  const listing = useSelector(
    (state: any) => state[`${listingName}`]["listing"]
  );
  const listingRushDays = useSelector(
    (state: any) => state[`${listingName}`]["listingRushDays"]
  );
  const searchText = useSelector(
    (state: any) => state[`${listingName}`]["searchText"]
  );
  const selected = useSelector(
    (state: any) => state[`${listingName}`]["selected"]
  );
  const operations = useSelector(
    (state: any) => state[`${listingName}`]["operations"]
  );

  async function fetchAll() {
    const filter: any = { deleted: { eq: "0" } };

    try {
      let nextToken = null;
      let listing: Transaction[] = [];
      let allListing: Transaction[] = [];

      do {
        const response: any = await API.graphql({
          query: listTransactions,
          variables: {
            limit: 10000,
            filter,
            nextToken: nextToken,
          },
          authMode: session
            ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
            : GRAPHQL_AUTH_MODE.AWS_IAM,
        });

        nextToken = response.data.listTransactions.nextToken;
        listing = response.data.listTransactions.items;

        allListing = [...allListing, ...listing];
      } while (nextToken !== null || nextToken !== undefined);

      return allListing;
    } catch (err) {
      showError(err);

      throw err;
    }
  }

  async function fetch(props: TransactionListingVariables) {
    try {
      let {
        conceptID,
        searchText,
        startIndex,
        limit,
        fromDate,
        toDate,
        conceptsSelectedFilters,
      } = props;

      const filter: any = { deleted: { eq: "0" } };
      filter.or = [];
      filter.and = [];

      if (searchText && searchText.length > 0) {
        searchText = searchText.trim().toLowerCase();
        filter.or = [
          { customerName: { contains: searchText } },
          { employeeName: { contains: searchText } },
          { checkNumber: { contains: searchText } },
        ];
      }

      if (conceptsSelectedFilters && conceptsSelectedFilters.length > 0) {
        let or = [];
        for (let filter of conceptsSelectedFilters) {
          or.push({ conceptID: { eq: filter.id } });
        }
        if (or.length > 0) filter.and.push({ or: or });
      } else if (conceptID) filter.conceptID = { eq: conceptID };

      if (operations && operations.length > 0) {
        let or = [];
        for (let filter of operations) {
          or.push({ points_operation: { eq: filter.id } });
        }
        if (or.length > 0) filter.and.push({ or: or });
      }

      if (fromDate && toDate) {
        const fromDateEdited = new Date(fromDate);
        fromDateEdited.setHours(0, 0, 0, 0);

        const toDatePlusOneDay = new Date(toDate);
        toDatePlusOneDay.setDate(toDatePlusOneDay.getDate() + 1);
        toDatePlusOneDay.setHours(0, 0, 0, 0);

        filter.createdAt = {
          between: [
            formatDateToYYYYMMDDHHMMSS(fromDateEdited),
            formatDateToYYYYMMDDHHMMSS(toDatePlusOneDay),
          ],
        };
      }

      if (filter.and && filter.and.length === 0) {
        delete filter.and;
      }
      if (filter.or && filter.or.length === 0) {
        delete filter.or;
      }
      try {
        const response: any = await API.graphql({
          query: listTransactions,
          variables: {
            limit: limit,
            filter,
            nextToken: null,
          },
          authMode: session
            ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
            : GRAPHQL_AUTH_MODE.AWS_IAM,
        });

        let nextToken = response.data.listTransactions.nextToken;
        let listing = response.data.listTransactions.items;

        dispatch(setListing(listing));
        return listing;
      } catch (err) {
        showError(err);
        return [];
      }
    } catch (err) {
      showError(err);
    }
  }

  async function fetchByCustomer(props: TransactionListingVariables) {
    try {
      let { customerID, searchText, startIndex, limit, fromDate, toDate } =
        props;

      const filter: any = { deleted: { eq: "0" } };
      filter.or = [];
      filter.and = [];

      if (searchText && searchText.length > 0) {
        searchText = searchText.trim().toLowerCase();
        filter.or = [
          { employeeName: { contains: searchText } },
          { checkNumber: { contains: searchText } },
        ];
      }

      if (operations && operations.length > 0) {
        let or = [];
        for (let filter of operations) {
          or.push({ points_operation: { eq: filter.id } });
        }
        if (or.length > 0) filter.and.push({ or: or });
      }

      if (fromDate && toDate) {
        const fromDateEdited = new Date(fromDate);
        fromDateEdited.setHours(0, 0, 0, 0);

        const toDatePlusOneDay = new Date(toDate);
        toDatePlusOneDay.setDate(toDatePlusOneDay.getDate() + 1);
        toDatePlusOneDay.setHours(0, 0, 0, 0);

        filter.createdAt = {
          between: [
            formatDateToYYYYMMDDHHMMSS(fromDateEdited),
            formatDateToYYYYMMDDHHMMSS(toDatePlusOneDay),
          ],
        };
      }

      if (filter.and && filter.and.length === 0) {
        delete filter.and;
      }
      if (filter.or && filter.or.length === 0) {
        delete filter.or;
      }
      try {
        const response: any = await API.graphql({
          query: transactionByUser,
          variables: {
            customerID,
            filter,
            limit: limit,
            nextToken: null,
          },
          authMode: session
            ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
            : GRAPHQL_AUTH_MODE.AWS_IAM,
        });

        let nextToken = response.data.transactionByUser.nextToken;
        let listing = response.data.transactionByUser.items;

        dispatch(setListing(listing));
        return listing;
      } catch (err) {
        showError(err);
        return [];
      }
    } catch (err) {
      showError(err);
    }
  }

  async function get(resourceId: string) {
    try {
      const transaction: any = await API.graphql({
        query: getTransaction,
        variables: { id: resourceId },
        authMode: session
          ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
          : GRAPHQL_AUTH_MODE.AWS_IAM,
      });

      return transaction.data.getTransaction;
    } catch (err) {
      showError(err);
      return null;
    }
  }

  async function rushDays(listing: Transaction[]) {
    try {
      let sundayCounter = 0;
      let mondayCounter = 0;
      let tuesdayCounter = 0;
      let wednesdayCounter = 0;
      let thursdayCounter = 0;
      let fridayCounter = 0;
      let saturdayCounter = 0;

      for (let i = 0; i < listing.length; i++) {
        let day = getDayName(listing[i].createdAt!.split("T")[0], "en-EG");

        switch (day) {
          case "Sun":
            sundayCounter = sundayCounter + 1;
            break;
          case "Mon":
            mondayCounter = mondayCounter + 1;
            break;
          case "Tue":
            tuesdayCounter = tuesdayCounter + 1;
            break;
          case "Wed":
            wednesdayCounter = wednesdayCounter + 1;
            break;
          case "Thu":
            thursdayCounter = thursdayCounter + 1;
            break;
          case "Fri":
            fridayCounter = fridayCounter + 1;
            break;
          default:
            saturdayCounter = saturdayCounter + 1;
        }
      }

      const rushDays = [
        {
          name: "Sun",
          value: sundayCounter,
          fill: "#F9204A",
        },
        {
          name: "Mon",
          value: mondayCounter,
          fill: "#37B44E",
        },
        {
          name: "Tue",
          value: tuesdayCounter,
          fill: "#FF5215",
        },
        {
          name: "Wed",
          value: wednesdayCounter,
          fill: "#E65339",
        },
        {
          name: "Thu",
          value: thursdayCounter,
          fill: "#000000",
        },
        {
          name: "Fri",
          value: fridayCounter,
          fill: "#3C8C95",
        },
        {
          name: "Sat",
          value: saturdayCounter,
          fill: "#0088FE",
        },
      ];

      return rushDays;
    } catch (err: Error | any) {
      showError(`Error fetching transactions ${err.message || ""}`);
    }
  }

  async function create(data: any) {
    try {
      const createInput: CreateTransactionInput = {
        accountID: data.accountID,
        customerID: data.customerID,
        checkNumber: data.checkNumber,
        orderTotal: data.orderTotal,
        employeeID: data.employeeID,
        employeeName: data.employeeName,
        revenueCentreName: data.revenueCentreName,
        revenueCentreID: data.revenueCentreID,
        points: data.points,
        points_operation: data.points_operation,
        paymentStatus: data.paymentStatus,
        deleted: "0",
        createdAt: new Date().toISOString(),
        createdByID: session.sub,
        createdByName: session.name,
      };

      let createdTransaction: any = await API.graphql<Transaction>({
        query: createTransaction,
        variables: { input: createInput },
        authMode: true
          ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
          : GRAPHQL_AUTH_MODE.AWS_IAM,
      });

      console.log({
        createdTransaction,
      });
      createdTransaction = createdTransaction.data.createTransaction;
      showConfirm(`New ${singleName} has been created successfully`);

      return createdTransaction;
    } catch (err) {
      showError(err);
    }
  }

  async function update(resourceId: string, data: any) {
    try {
      const original = await get(resourceId);
      if (!original) {
        showError("Can not find this user.");
        return;
      }

      const transactionInput: UpdateTransactionInput = {
        id: original.id,
        accountID: data.accountID ? data.accountID : original!.accountID,
        conceptID: data.conceptID ? data.conceptID : original!.conceptID,

        customerID: data.customerID ? data.customerID : original!.customerID,
        checkNumber: data.checkNumber
          ? data.checkNumber
          : original!.checkNumber,
        orderTotal: data.orderTotal ? data.orderTotal : original!.orderTotal,
        employeeID: data.employeeID ? data.employeeID : original!.employeeID,
        employeeName: data.employeeName
          ? data.employeeName
          : original!.employeeName,
        revenueCentreName: data.revenueCentreName
          ? data.revenueCentreName
          : original!.revenueCentreName,
        revenueCentreID: data.revenueCentreID
          ? data.revenueCentreID
          : original!.revenueCentreID,
        points: data.points ? data.points : original!.points,
        points_operation: data.points_operation
          ? data.points_operation
          : original!.points_operation,
        paymentStatus: data.paymentStatus
          ? data.paymentStatus
          : original!.paymentStatus,
        createdAt: data.createdAt ? data.createdAt : original!.createdAt,
      };

      let updatedTransaction: any = await API.graphql<Transaction>({
        query: updateTransaction,
        variables: { input: transactionInput },
        authMode: true
          ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
          : GRAPHQL_AUTH_MODE.AWS_IAM,
      });
      console.log({
        updatedTransaction,
      });

      showConfirm(`${singleName} has been updated successfully`);

      return updatedTransaction;
    } catch (err) {
      showError(err);
    }
  }

  async function trash(resourceId: string) {
    try {
      const original = await get(resourceId);

      const transactionInput: UpdateTransactionInput = {
        id: original.id,
        deleted: "1",
      };

      let updatedTransaction: any = await API.graphql<Transaction>({
        query: updateTransaction,
        variables: { input: transactionInput },
        authMode: true
          ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
          : GRAPHQL_AUTH_MODE.AWS_IAM,
      });

      showConfirm(`${singleName} has been moved to trash successfully`);

      return updatedTransaction;
    } catch (err) {
      showError(err);
    }
  }

  async function bulkTrash(resourceIds: any) {
    for (let resourceId of resourceIds) {
      try {
        await trash(resourceId);
      } catch (err: Error | any) {
        showError(err);
      }
    }

    dispatch(
      setListing(
        listing.filter((resource: any) => !resourceIds.has(resource.id))
      )
    );

    showConfirm(
      `${resourceIds.size} ${listingName} items has been moved to trash`
    );
  }

  async function remove(resourceId: any) {
    try {
      const removedTransaction = await API.graphql({
        query: deleteTransaction,
        variables: {
          input: {
            id: resourceId,
          },
        },
        authMode: GRAPHQL_AUTH_MODE.AWS_IAM,
      });

      dispatch(
        setListing(
          listing.filter((resource: any) => resource.id !== resourceId)
        )
      );

      showConfirm(`${singleName} has been deleted successfully`);
    } catch (err: Error | any) {
      console.log(err);
      showError(err);
    }
  }

  async function exportAll(params: TransactionListingVariables) {
    const transactions = await fetch(params);

    let exportedData = [];

    for (let transaction of transactions!) {
      let row: any = { ...transaction };

      if (transaction.conceptID) {
        row.conceptID = conceptsGetName(row.conceptID);
      }
      if (transaction.customerID) {
        row.customerID = guestsGetName(row.customerID);
      }

      if (transaction.itemNames) {
        row.itemNames = transaction.itemNames.join(", ");
      }

      /* Check Payment Type */
      let type = "";
      if (row.points_operation === LoyaltyOperations.REWARD_POINTS) {
        type = "Full";
      } else {
        if (parseFloat(row.orderTotal) === parseFloat(row.paidAmount)) {
          type = "Full";
        } else {
          type = `Partial ${
            row.paidAmount !== null
              ? "(EGP" + row.paidAmount.toFixed(2) + ")"
              : ""
          }`;
        }
      }
      row.paymentType = type;

      exportedData.push(row);
    }

    return exportedData;
  }

  /**
   * Fix trx payment status
   *
   * @returns void
   */
  async function fixTransactionData() {
    try {
      const transactions: Transaction[] = await fetchAll();
      console.log({ transactions });

      if (transactions)
        for (let trx of transactions) {
          let type = "";
          if (trx.points_operation === LoyaltyOperations.REWARD_POINTS) {
            type = "Full";
          } else {
            if (
              trx.paidAmount &&
              parseFloat(trx.orderTotal) === trx.paidAmount
            ) {
              type = "Full";
            } else {
              type = "Partial";
            }
          }

          await update(trx.id, {
            paymentStatus: type,
            createdAt: new Date(trx.createdAt).toISOString(),
          });
        }

      showConfirm(`${singleName} has been updated successfully`);
    } catch (err) {
      showError(err);
    }
  }

  async function addConceptID() {
    try {
      const transactions = await fetchAll();
      if (transactions)
        for (let trx of transactions) {
          await update(trx.id, {
            conceptID: "3ba82605-d8d3-49f9-aeee-aa187de7f2d3",
          });
        }

      showConfirm(`${singleName} has been updated successfully`);
    } catch (err) {
      showError(err);
    }
  }

  const headCells: readonly HeadCell[] = [
    {
      id: "conceptID",
      numeric: false,
      disablePadding: false,
      label: "Concept",
    },
    {
      id: "customerID",
      numeric: false,
      disablePadding: false,
      label: "Customer",
    },
    {
      id: "checkNumber",
      numeric: false,
      disablePadding: false,
      label: "Check Num.",
    },
    {
      id: "itemNames",
      numeric: false,
      disablePadding: false,
      label: "Items",
    },
    {
      id: "orderTotal",
      numeric: false,
      disablePadding: false,
      label: "Order Total",
    },
    {
      id: "paidAmount",
      numeric: false,
      disablePadding: false,
      label: "Paid Amount",
    },
    {
      id: "points",
      numeric: false,
      disablePadding: false,
      label: "Points",
    },
    {
      id: "paymentStatus",
      numeric: false,
      disablePadding: false,
      label: "Payment Status",
    },
    {
      id: "revenueCentreName",
      numeric: false,
      disablePadding: false,
      label: "Revenue Center",
    },
    {
      id: "employeeName",
      numeric: false,
      disablePadding: false,
      label: "Employee Name",
    },
    {
      id: "points_operation",
      numeric: false,
      disablePadding: false,
      label: "Operation",
    },
    {
      id: "createdBy",
      numeric: false,
      disablePadding: false,
      label: "Created By",
    },
    {
      id: "createdAt",
      numeric: false,
      disablePadding: false,
      label: "Date",
    },
    {
      id: "actions",
      numeric: true,
      disablePadding: false,
      label: "",
    },
  ];

  const dataCells: readonly string[] = [
    "conceptID",
    "customerID",
    "checkNumber",
    "itemNames",
    "orderTotal",
    "paidAmount",
    "points",
    "paymentStatus",
    "revenueCentreName",
    "employeeName",
    "points_operation",
  ];

  const options: any[] = [];

  for (let option of listing) {
    options.push({ label: option.name, value: option.id });
  }

  const api: any = {};

  api[`${listingName}Listing`] = listing;
  api[`${listingName}ListingRushDays`] = listingRushDays;
  api[`${listingName}Operations`] = operations;
  api[`${listingName}Options`] = options;
  api[`${listingName}SearchText`] = searchText;
  api[`${listingName}HeadCells`] = headCells;
  api[`${listingName}DataCells`] = dataCells;
  api[`${listingName}Selected`] = selected;
  api[`${listingName}Fetch`] = fetch;
  api[`${listingName}FetchAll`] = fetchAll;
  api[`${listingName}FetchByCustomer`] = fetchByCustomer;
  api[`${listingName}FetchRushDays`] = rushDays;
  api[`${listingName}Get`] = get;
  api[`${listingName}Create`] = create;
  api[`${listingName}Update`] = update;
  api[`${listingName}AddConceptID`] = addConceptID;
  api[`${listingName}Trash`] = trash;
  api[`${listingName}BulkTrash`] = bulkTrash;
  api[`${listingName}Delete`] = remove;
  api[`${listingName}Export`] = exportAll;
  api[`${listingName}FixTransactionData`] = fixTransactionData;
  api[`${listingName}ChangeListing`] = (listing: Transaction[]) =>
    dispatch(setListing(listing));
  api[`${listingName}ChangeListingRushDays`] = (listing: any[]) =>
    dispatch(setListingRushDays(listing));
  api[`${listingName}Search`] = (searchText: string) =>
    dispatch(setSearchText(searchText));
  api[`${listingName}Select`] = (conceptID: string) =>
    dispatch(setSelected(conceptID));
  api[`${listingName}ChangeOperationFilters`] = (operations: any[]) =>
    dispatch(setOperationFilters(operations));

  return api;
};

export default useResource;
